/**
 * ResetPasswordPage.tsx — MER password-reset confirmation. Phase-2 P2-D3.
 *
 * ── Token handling: THE TOKEN ARRIVES IN THE FRAGMENT ────────────────────────
 * The link is /reset-password#token=... and the '#' is load-bearing. A fragment
 * is never sent to the server, so the token cannot reach an access log, a proxy,
 * or a platform request-path store. It previously arrived in the QUERY STRING,
 * and that wrote the bearer token verbatim into the hosting platform's request
 * logs on every reset — once for this page load and once for the validation
 * call. replaceState did not prevent that and could not have: both requests had
 * already been sent by the time any client code ran.
 *
 * The remaining exposures are handled here rather than tolerated:
 *   - the token is read into memory once and the fragment is immediately
 *     removed with history.replaceState, so it does not linger in the address
 *     bar or the history entry;
 *   - it is held in a ref, never in state, and never in localStorage or
 *     sessionStorage — persistent client storage would outlive the reset and is
 *     readable by any script that later runs on this origin;
 *   - the page loads no third-party asset, and sets no-referrer, so there is
 *     nothing to leak it to.
 *
 * ── Nothing mutates on load ──────────────────────────────────────────────────
 * Validation is a non-consuming POST — POST for the transport property, not the
 * semantics, because a request body is not written to request-path logs. Mail
 * systems prefetch links, and a consuming load would let a scanner burn the
 * token; with the token in the fragment a prefetcher never receives it at all.
 * The password only changes on the explicit confirmation POST.
 *
 * ── No automatic sign-in ─────────────────────────────────────────────────────
 * A successful reset sends the user to the sign-in page. Auto-login from an
 * emailed link would turn the link into a credential.
 */
import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { csrfHeader } from '../../auth/AuthContext';

const SHELL: React.CSSProperties = {
  background: '#080F1C',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: "'Courier New', Courier, monospace",
  backgroundImage: `linear-gradient(rgba(26,48,80,0.15) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(26,48,80,0.15) 1px, transparent 1px)`,
  backgroundSize: '40px 40px',
};
const LABEL: React.CSSProperties = { fontSize: 10, letterSpacing: 2, color: '#3A5070', display: 'block', marginBottom: 6 };
const INPUT: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 13,
  background: '#0A1628', border: '1px solid #1A3050',
  borderRadius: 3, color: '#E8EDF5', fontFamily: 'inherit', outline: 'none',
};

type Phase = 'checking' | 'ready' | 'invalid' | 'unavailable' | 'done';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const tokenRef = useRef<string>('');
  const [phase, setPhase]     = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  // Referrer-Policy as a document-level meta, because the strongest form — the
  // response header — would have to be set in server.js, which is byte-pinned by
  // a banked contract test. With the token in the fragment it can no longer
  // appear in a Referer at all, so this is defence in depth, not the control.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    // The fragment, NOT the query string — see the note at the top of this file.
    // A leading '#' is stripped; anything before '=' that is not our key is
    // ignored rather than guessed at.
    const hash   = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const token  = new URLSearchParams(hash).get('token') ?? '';
    tokenRef.current = token;

    // Strip the token from the visible URL and the history entry immediately.
    if (token) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!token) { setPhase('invalid'); return; }

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/auth/password-reset/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        if (r.status === 404) { setPhase('unavailable'); return; }
        const body = await r.json().catch(() => ({}));
        setPhase(body?.valid === true ? 'ready' : 'invalid');
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const r = await fetch('/api/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ token: tokenRef.current, password }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body?.ok) {
        setPhase('done');
        setTimeout(() => navigate('/login', { replace: true }), 2500);
        return;
      }
      if (body?.reason === 'INVALID_TOKEN') { setPhase('invalid'); return; }
      setError(body?.detail ?? 'Could not reset the password. Please request a new link.');
    } catch {
      setError('Network error — check your connection.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mer-full-page" style={SHELL}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 10, letterSpacing: 4, color: '#C9A84C', fontWeight: 700 }}>MER ALGO FOOTPRINTS</div>
          <div style={{ fontSize: 12, letterSpacing: 2, color: '#3A5070', marginTop: 10 }}>CHOOSE A NEW PASSWORD</div>
        </div>

        {phase === 'checking' && (
          <div style={{ fontSize: 12, color: '#3A5070' }}>Checking link…</div>
        )}

        {phase === 'unavailable' && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            Password reset is not currently available. Please contact an administrator.
          </div>
        )}

        {phase === 'invalid' && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            This link is no longer valid. Reset links expire and can only be used
            once.{' '}
            <Link to="/forgot-password" style={{ color: '#C9A84C', textDecoration: 'none' }}>
              Request a new one →
            </Link>
          </div>
        )}

        {phase === 'done' && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            Your password has been changed. Signing you out everywhere — please
            sign in again.
          </div>
        )}

        {phase === 'ready' && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="rp-password" style={LABEL}>NEW PASSWORD</label>
              <input
                id="rp-password" type="password" value={password} required minLength={8}
                autoComplete="new-password" onChange={e => setPassword(e.target.value)} style={INPUT}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="rp-confirm" style={LABEL}>CONFIRM PASSWORD</label>
              <input
                id="rp-confirm" type="password" value={confirm} required minLength={8}
                autoComplete="new-password" onChange={e => setConfirm(e.target.value)} style={INPUT}
              />
            </div>
            {error && <div style={{ fontSize: 11, color: '#C0554E', marginBottom: 12 }}>{error}</div>}
            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '12px', fontSize: 11, fontWeight: 700, letterSpacing: 2,
                background: loading ? '#6A5428' : '#C9A84C', color: '#080F1C',
                border: 'none', borderRadius: 3, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {loading ? 'SAVING…' : 'SET NEW PASSWORD'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: '#3A5070' }}>
          <Link to="/login" style={{ color: '#3A5070', textDecoration: 'none' }}>← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
